import { ICategoryPickerFields, IReviewList } from '../../types/@types/generated/contentful';
import { EVENT_NAME, sendServerEvent } from '../analytics';
import { getClient, getPreviewClient } from '../contentfulSetup';
import { captureException } from '../tracking/sentry/sentry';

export const getEntry = async <T>(entryId: string, query?: unknown) => {
  const client = getClient();
  return await client.getEntry<T>(entryId, query);
};

export const getEntryPreview = async <T>(entryId: string, query?: unknown) => {
  const client = getPreviewClient();
  return await client.getEntry<T>(entryId, query);
};

export const getEntries = async <T>(query?: unknown) => {
  const client = getClient();
  return await client.getEntries<T>(query);
};

export const getReviewListBySlug = async ({ slug }: { slug: string }) => {
  try {
    return await getByUniqueSlug<IReviewList>({ slug, contentType: 'reviewList' });
  } catch (e) {
    captureException(e);
    return;
  }
};

export const getByUniqueSlug = async <T>({
  slug,
  contentType,
  include,
  tags,
  locale,
}: {
  slug: string;
  contentType: string;
  include?: number;
  tags?: string[];
  locale?: string;
}) => {
  try {
    // add search-parameters filter while request entries,
    // [all] mean all tags need to be matched, [in] means at least one matching tag
    const tagsFilter = tags && tags.length ? { 'fields.tags[all]': tags.join(',') } : null;
    const entries = await getClient().getEntries({
      content_type: contentType,
      'fields.slug': slug,
      include,
      ...tagsFilter,
      locale,
    });

    if (!entries.items.length) {
      // Don't send error to sentry if it's just a missing page
      if (contentType === 'landingPage') {
        sendServerEvent({
          name: EVENT_NAME.HTTP_NOT_OK,
          properties: {
            statusCode: 404,
            statusDescription: 'not found',
            url: `https://www.milkywire.com/${slug}`,
          },
        });
        return;
      }

      captureException(`No entry found for content type ${contentType} and slug ${slug}`);
      return;
    }
    if (entries.items.length > 1) {
      captureException(
        `More than one entry found for content type ${contentType} and slug ${slug}`,
      );
      return;
    }
    return (entries.items[0] as unknown) as T;
  } catch (e) {
    captureException(e);
    return;
  }
};

export async function getCategoryPicker(slug: string) {
  try {
    const entries = await getClient().getEntries<ICategoryPickerFields>({
      content_type: 'categoryPicker',
      'fields.slug': slug,
    });

    return entries.items[0];
  } catch (error) {
    captureException(error);
    return;
  }
}

export async function hubSpotSubmit({
  url,
  body,
  successMessage,
  errorMessage,
}: {
  url: string;
  body: string;
  successMessage: string;
  errorMessage: string;
}) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (response.status > 299) {
      throw Error(`Error for ${url}, response status: ${response.status}`);
    }

    return successMessage;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    captureException(error);
    return errorMessage;
  }
}
